export interface SystemInfo {
    id: string;
    serialNumber: string;
    timestamp: string;
    hostname?: string;
    ipAddress?: string;
    macAddress?: string;
    osVersion?: string;
    hauptwerkVersion?: string;
    [key: string]: any;
}

export interface DiagnosticCommand {
    id: string;
    status: string;
    createdAtUtc: string;
    output?: string;
}

export interface DiagnosticStatus {
    command: DiagnosticCommand | null;
    snapshotCommandId: string | null;
}

export interface DiagnosticSnapshot {
    agentVersion: string;
    collectedAtUtc: string;
    logs: {
        source: string;
        fileName: string;
        status: 'collected' | 'missing' | 'error' | 'limit';
        originalBytes: number | null;
        lastWriteTimeUtc: string | null;
        truncated: boolean;
        content: string | null;
        error: string | null;
    }[];
}

export function normalizeDiagnosticSnapshot(value: unknown): DiagnosticSnapshot | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const read = (object: Record<string, unknown>, name: string): unknown => {
        const key = Object.keys(object).find((candidate) => candidate.toLowerCase() === name.toLowerCase());
        return key ? object[key] : undefined;
    };
    const root = value as Record<string, unknown>;
    const wrapped = read(root, 'snapshot');
    const snapshot = wrapped && typeof wrapped === 'object'
        ? wrapped as Record<string, unknown>
        : root;
    const agentVersion = read(snapshot, 'agentVersion');
    const collectedAtUtc = read(snapshot, 'collectedAtUtc');
    const rawLogs = read(snapshot, 'logs');

    if (typeof agentVersion !== 'string'
        || typeof collectedAtUtc !== 'string'
        || !Array.isArray(rawLogs)) {
        return null;
    }

    const logs: DiagnosticSnapshot['logs'] = [];
    for (const rawLog of rawLogs) {
        if (!rawLog || typeof rawLog !== 'object') {
            return null;
        }
        const entry = rawLog as Record<string, unknown>;
        const source = read(entry, 'source');
        const fileName = read(entry, 'fileName');
        const status = read(entry, 'status');
        const originalBytes = read(entry, 'originalBytes');
        const lastWriteTimeUtc = read(entry, 'lastWriteTimeUtc');
        const truncated = read(entry, 'truncated');
        const content = read(entry, 'content');
        const error = read(entry, 'error');

        if (typeof source !== 'string'
            || typeof fileName !== 'string'
            || !['collected', 'missing', 'error', 'limit'].includes(String(status))
            || !(typeof originalBytes === 'number' || originalBytes === null || originalBytes === undefined)
            || !(typeof lastWriteTimeUtc === 'string' || lastWriteTimeUtc === null || lastWriteTimeUtc === undefined)
            || !(typeof truncated === 'boolean' || truncated === undefined)
            || !(typeof content === 'string' || content === null || content === undefined)
            || !(typeof error === 'string' || error === null || error === undefined)) {
            return null;
        }

        logs.push({
            source,
            fileName,
            status: status as DiagnosticSnapshot['logs'][number]['status'],
            originalBytes: typeof originalBytes === 'number' ? originalBytes : null,
            lastWriteTimeUtc: typeof lastWriteTimeUtc === 'string' ? lastWriteTimeUtc : null,
            truncated: truncated === true,
            content: typeof content === 'string' ? content : null,
            error: typeof error === 'string' ? error : null,
        });
    }

    return { agentVersion, collectedAtUtc, logs };
}

export interface Registration {
    id: string;
    serialNumber: string;
    customer: string;
    customerPhone?: string;
    customerEmail?: string;
    registeredAt: string;
    firstReportAt?: string;
    macAddress?: string;
    isActive: boolean;
    lastSeenAt?: string;
    /** Last ContentAgent poll (~every minute while the PC is running). */
    agentLastSeenAt?: string;
    /** ContentAgent version reported on its last poll (only sent by agent 1.0.3+). */
    agentVersion?: string;
    clientCity?: string;
    teamViewerId?: string;
    notes?: string;
}

export interface DashboardItem {
    serialNumber: string;
    customer?: string;
    timestamp: string;
    hostName?: string;
    organ?: string;
    vpo?: string;
}

export type TicketStatus = 'new' | 'in_progress' | 'waiting_customer' | 'done';
export type TicketMessageChannel = 'whatsapp' | 'mail' | 'internal';
export type TicketMessageDirection = 'customer' | 'agent';

export interface TicketMessage {
    id: string;
    channel: TicketMessageChannel | string;
    direction: TicketMessageDirection | string;
    text: string;
    author?: string;
    createdAt: string;
    image?: TicketImage;
}

export interface TicketImage {
    fileName: string;
    contentType: string;
    size: number;
}

export interface Ticket {
    id: string;
    ticketNumber?: number;
    serialNumber?: string;
    customerName: string;
    customerPhone?: string;
    subject: string;
    status: TicketStatus;
    assignedTo?: string;
    notes?: string;
    messages?: TicketMessage[];
    createdAt: string;
    updatedAt: string;
}

export interface CreateTicketRequest {
    serialNumber: string;
    customerName?: string;
    subject: string;
    assignedTo?: string;
    notes?: string;
}

export interface UpdateRegistrationRequest {
    customer?: string;
    customerPhone?: string;
    customerEmail?: string;
    notes?: string;
}

export interface CreateRegistrationRequest {
    serialNumber: string;
    customer: string;
    customerPhone?: string;
    customerEmail?: string;
    notes?: string;
}

export interface AssigneeOption {
    email: string;
    name: string;
}

export interface AddTicketMessageRequest {
    channel?: TicketMessageChannel;
    direction?: TicketMessageDirection;
    text: string;
    author?: string;
    image?: {
        fileName: string;
        contentType: string;
        dataBase64: string;
    };
}